import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { gql } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import { setToken, getToken } from '../lib/apollo'

const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      firstName
      lastName
      fullName
      phone
      avatarUrl
      locale
      role
      admin
      status
      emailVerified
    }
  }
`

export interface CurrentUser {
  id: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  phone: string | null
  avatarUrl: string | null
  locale: string
  role: 'customer' | 'provider'
  admin: boolean | null
  status: string
  emailVerified: boolean
}

interface AuthContextValue {
  currentUser: CurrentUser | null
  loading: boolean
  signIn: (token: string, user: CurrentUser) => Promise<CurrentUser>
  signOut: () => void
  refetchUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const apolloClient = useApolloClient()
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)

  const loadCurrentUser = useCallback(async () => {
    const { data } = await apolloClient.query<{ me: CurrentUser }>({
      query: ME_QUERY,
      fetchPolicy: 'network-only',
    })

    return data?.me ?? null
  }, [apolloClient])

  const signIn = useCallback(async (token: string, user: CurrentUser) => {
    setToken(token)
    setCurrentUser(user)

    try {
      const nextUser = await loadCurrentUser()
      if (nextUser) {
        setCurrentUser(nextUser)
        return nextUser
      }
    } catch {
      // Fall back to the mutation payload if the follow-up me query fails.
    }

    return user
  }, [loadCurrentUser])

  const signOut = useCallback(() => {
    setToken(null)
    setCurrentUser(null)
    apolloClient.clearStore()
  }, [apolloClient])

  const refetchUser = useCallback(async () => {
    if (!currentUser) return
    try {
      const nextUser = await loadCurrentUser()
      if (nextUser) setCurrentUser(nextUser)
    } catch {
      signOut()
    }
  }, [currentUser, loadCurrentUser, signOut])

  // On mount, restore session from persisted token
  useEffect(() => {
    const savedToken = getToken()
    if (savedToken) {
      loadCurrentUser()
        .then((user) => {
          if (user) setCurrentUser(user)
          else setToken(null)
        })
        .catch(() => setToken(null))
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [loadCurrentUser])

  return (
    <AuthContext.Provider value={{ currentUser, loading, signIn, signOut, refetchUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function useRequireAuth(role?: CurrentUser['role']) {
  const { currentUser, loading } = useAuth()
  return { currentUser, loading, authorized: !loading && !!currentUser && (!role || currentUser.role === role) }
}
